import {
    collection,
    addDoc,
    updateDoc,
    doc,
    serverTimestamp,
    getDoc,
    getDocs,
    query,
    where,
    orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { secureGetItem } from '../../data/utils';

const COLLECTION_NAME = 'CatalogOrders';

/**
 * Creates a new catalog order with initial status
 * @param {string} clientId - The client ID
 * @param {string} catalogRef - Reference to the catalog
 * @param {string} type - Order type ('add_assets_to_catalogue' or 'order_3d_asset')
 * @returns {Promise<string>} - The document ID of the created order
 */
export const createCatalogOrder = async (clientId, catalogRef = null, type = 'add_assets_to_catalogue') => {
    try {
        const userEmail = await secureGetItem('email');
        if (!userEmail) {
            throw new Error('User email not found in secure storage');
        }

        const normalizedEmail = userEmail.toLowerCase(); // Normalizza l'email

        const orderData = {
            timestamp: serverTimestamp(),
            status: 'pending_analysis',
            list_3d_assets_results: [],
            profile: normalizedEmail,
            clientId: clientId,
            type: type,
            catalogRef: catalogRef
        };

        const docRef = await addDoc(collection(db, COLLECTION_NAME), orderData);
        const orderId = docRef.id;

        // Add order to Profile's list_orders
        try {
            await addOrderToProfile(normalizedEmail, orderId);
        } catch (error) {
            console.error('Failed to add order to profile, but order was created:', error);
        }

        // Add order to Client's list_orders
        if (clientId) {
            try {
                await addOrderToClient(clientId, orderId);
            } catch (error) {
                console.error('Failed to add order to client, but order was created:', error);
            }
        }

        return orderId;
    } catch (error) {
        console.error('Error creating catalog order:', error);
        throw error;
    }
};

/**
 * Creates a new 3D asset order for quote requests
 * @param {string} clientId - The client ID
 * @returns {Promise<string>} - The document ID of the created order
 */
export const createAssetOrder = async (clientId) => {
    return createCatalogOrder(clientId, null, 'order_3d_asset');
};

/**
 * Updates the catalog order with analysis results
 * @param {string} orderId - The order document ID
 * @param {Array} analysisResults - Array of analysis results
 * @returns {Promise<void>}
 */
export const updateCatalogOrderWithAnalysis = async (orderId, analysisResults) => {
    try {
        const orderRef = doc(db, COLLECTION_NAME, orderId);

        // Transform the analysis results to match our schema
        const transformedResults = analysisResults.map(item => ({
            skuCode: item.skuCode || null,
            eanCode: item.eanCode || null,
            upcCode: item.upcCode || null,
            result: item.result || 'invalid', // found, orderable, in_production, invalid
            isSelected: false // Initially not selected
        }));

        const updateData = {
            status: 'analysis_complete',
            list_3d_assets_results: transformedResults
        };

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('Error updating catalog order:', error);
        throw error;
    }
};

/**
 * Updates the catalog order status
 * @param {string} orderId - The order document ID
 * @param {string} status - New status (pending_analysis, analysis_complete, processing_addition, completed, error)
 * @returns {Promise<void>}
 */
export const updateCatalogOrderStatus = async (orderId, status) => {
    try {
        const orderRef = doc(db, COLLECTION_NAME, orderId);

        const updateData = {
            status: status
        };

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('Error updating catalog order status:', error);
        throw error;
    }
};

/**
 * Updates the catalog order with available and orderable items in list_3d_assets_results
 * @param {string} orderId - The order document ID
 * @param {Array} analysisResults - Array of analysis results
 * @returns {Promise<void>}
 */
export const updateCatalogOrderWithAvailableAndOrderableItems = async (orderId, analysisResults) => {
    try {

        const orderRef = doc(db, COLLECTION_NAME, orderId);

        // Filter available and orderable items and transform them
        const availableAndOrderableItems = analysisResults
            .filter(item => {
                return item.result === 'Available' || item.result === 'Orderable';
            })
            .map(item => ({
                skuCode: item.skuCode || null,
                eanCode: item.eanCode || null,
                upcCode: item.upcCode || null,
                result: item.result,
                // Available items are selected by default (priority), Orderable are not
                isSelected: item.result === 'Available'
            }));

        const updateData = {
            status: 'analysis_complete',
            list_3d_assets_results: availableAndOrderableItems
        };

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('❌ Error updating catalog order with available and orderable items:', error);
        throw error;
    }
};

/**
 * Updates the catalog order with final selections before processing addition
 * @param {string} orderId - The order document ID
 * @param {Array} selectedProducts - Array of selected products from UI
 * @param {string} catalogId - The selected catalog ID
 * @returns {Promise<void>}
 */
export const updateCatalogOrderForAddition = async (orderId, selectedProducts, catalogId) => {
    try {

        // Get current catalog order to retrieve existing list_3d_assets_results
        const currentOrder = await getCatalogOrder(orderId);
        const existingResults = currentOrder.list_3d_assets_results || [];

        // Create a map of selected products for quick lookup
        const selectedProductsMap = new Map();
        selectedProducts.forEach(product => {
            // Create multiple keys for lookup (skuCode, eanCode, upcCode)
            if (product.skuCode) selectedProductsMap.set(`sku:${product.skuCode}`, true);
            if (product.eanCode) selectedProductsMap.set(`ean:${product.eanCode}`, true);
            if (product.upcCode) selectedProductsMap.set(`upc:${product.upcCode}`, true);
        });

        // Update isSelected for all items based on user selection
        const updatedResults = existingResults.map(item => {
            const isSelected = (
                (item.skuCode && selectedProductsMap.has(`sku:${item.skuCode}`)) ||
                (item.eanCode && selectedProductsMap.has(`ean:${item.eanCode}`)) ||
                (item.upcCode && selectedProductsMap.has(`upc:${item.upcCode}`))
            );

            return {
                ...item,
                isSelected: isSelected
            };
        });

        const orderRef = doc(db, COLLECTION_NAME, orderId);
        const updateData = {
            list_3d_assets_results: updatedResults,
            catalogRef: catalogId,
            status: 'processing_addition'
        };

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('❌ Error updating catalog order for addition:', error);
        throw error;
    }
};

/**
 * Gets a catalog order by ID
 * @param {string} orderId - The order document ID
 * @returns {Promise<Object>} - The order data
 */
export const getCatalogOrder = async (orderId) => {
    try {
        const orderRef = doc(db, COLLECTION_NAME, orderId);
        const orderSnap = await getDoc(orderRef);

        if (orderSnap.exists()) {
            return { id: orderSnap.id, ...orderSnap.data() };
        } else {
            throw new Error('Catalog order not found');
        }
    } catch (error) {
        console.error('Error getting catalog order:', error);
        throw error;
    }
};

/**
 * Gets all catalog orders for a specific user profile email
 * @param {string} profileEmail - The profile email to filter orders by
 * @returns {Promise<Array>} - Array of catalog orders
 */
export const getCatalogOrdersByProfile = async (profileEmail) => {
    try {
        if (!profileEmail) {
            throw new Error('Profile email is required');
        }

        const normalizedEmail = profileEmail.toLowerCase(); // Normalizza l'email

        // Query orders by profile email, ordered by timestamp descending (newest first)
        const ordersQuery = query(
            collection(db, COLLECTION_NAME),
            where('profile', '==', normalizedEmail),
            orderBy('timestamp', 'desc')
        );

        const querySnapshot = await getDocs(ordersQuery);

        const orders = [];
        querySnapshot.forEach((doc) => {
            orders.push({
                id: doc.id,
                ...doc.data()
            });
        });

        return orders;
    } catch (error) {
        console.error('Error getting catalog orders by profile:', error);
        throw error;
    }
};

/**
 * Updates the asset order with orderable and error items for quote processing
 * @param {string} orderId - The order document ID
 * @param {Array} analysisResults - Array of analysis results
 * @returns {Promise<void>}
 */
export const updateAssetOrderWithOrderableItems = async (orderId, analysisResults) => {
    try {

        const orderRef = doc(db, COLLECTION_NAME, orderId);

        // Filter orderable and error items for quote processing
        const orderableAndErrorItems = analysisResults
            .filter(item => {
                return item.result === 'Orderable' || item.result === 'Error' || item.result === 'In Progress';
            })
            .map(item => ({
                skuCode: item.skuCode || null,
                eanCode: item.eanCode || null,
                upcCode: item.upcCode || null,
                result: item.result,
                // Orderable items are not selected by default - user must choose
                isSelected: false
            }));

        const updateData = {
            status: 'analysis_complete',
            list_3d_assets_results: orderableAndErrorItems
        };

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('❌ Error updating asset order with orderable items:', error);
        throw error;
    }
};

/**
 * Updates the asset order with final selections for quote processing
 * @param {string} orderId - The order document ID
 * @param {Object|Array} orderData - Order data object with products and additionalInfo, or legacy array of selected products
 * @returns {Promise<void>}
 */
export const updateAssetOrderForQuote = async (orderId, orderData) => {
    try {
        // Handle both legacy format (array) and new format (object)
        let selectedProducts, additionalInfo;

        if (Array.isArray(orderData)) {
            // Legacy format - just selected products array
            selectedProducts = orderData;
            additionalInfo = null;
        } else {
            // New format - object with products and additionalInfo
            selectedProducts = orderData.products || [];
            additionalInfo = orderData.additionalInfo || null;
        }

        // Get current asset order to retrieve existing list_3d_assets_results
        const currentOrder = await getCatalogOrder(orderId);
        const existingResults = currentOrder.list_3d_assets_results || [];

        // Create a map of selected products for quick lookup
        const selectedProductsMap = new Map();
        selectedProducts.forEach(product => {
            // Create multiple keys for lookup (skuCode, eanCode, upcCode)
            if (product.skuCode) selectedProductsMap.set(`sku:${product.skuCode}`, true);
            if (product.eanCode) selectedProductsMap.set(`ean:${product.eanCode}`, true);
            if (product.upcCode) selectedProductsMap.set(`upc:${product.upcCode}`, true);
        });

        // Update isSelected for all items based on user selection
        const updatedResults = existingResults.map(item => {
            const isSelected = (
                (item.skuCode && selectedProductsMap.has(`sku:${item.skuCode}`)) ||
                (item.eanCode && selectedProductsMap.has(`ean:${item.eanCode}`)) ||
                (item.upcCode && selectedProductsMap.has(`upc:${item.upcCode}`))
            );

            return {
                ...item,
                isSelected: isSelected
            };
        });

        const orderRef = doc(db, COLLECTION_NAME, orderId);
        const updateData = {
            list_3d_assets_results: updatedResults,
            status: 'quote_requested'
        };

        // Add additional info if provided
        if (additionalInfo) {
            if (additionalInfo.batch) {
                updateData.batch = additionalInfo.batch;
            }
            if (additionalInfo.name) {
                updateData.orderName = additionalInfo.name;
            }
            if (additionalInfo.image) {
                updateData.image = additionalInfo.image;
            }
            if (additionalInfo.orderId) {
                updateData.orderId = additionalInfo.orderId;
            }
        }

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('❌ Error updating asset order for quote:', error);
        throw error;
    }
};

/**
 * Gets user profiles from Profiles collection
 * @returns {Promise<Object>} - Object with email as key and profile data as value
 */
export const getUserProfiles = async () => {
    try {

        const profilesQuery = query(collection(db, 'Profiles'));
        const querySnapshot = await getDocs(profilesQuery);

        const profiles = {};
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            // Handle both _email and email fields
            const email = data._email || data.email;
            if (email) {
                profiles[email] = {
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    role: data.role || '',
                    id: doc.id
                };
            }
        });
        return profiles;
    } catch (error) {
        console.error('Error getting user profiles:', error);
        throw error;
    }
};

/**
 * Updates the list_orders in a Profile document
 * @param {string} profileEmail - The profile email
 * @param {string} orderId - The order ID to add
 * @returns {Promise<void>}
 */
export const addOrderToProfile = async (profileEmail, orderId) => {
    try {
        const normalizedEmail = profileEmail.toLowerCase(); // Normalizza l'email

        // Find the profile document by email
        const profilesQuery = query(
            collection(db, 'Profiles'),
            where('_email', '==', normalizedEmail)
        );
        const querySnapshot = await getDocs(profilesQuery);

        if (querySnapshot.empty) {
            console.warn('Profile not found for email:', normalizedEmail);
            return;
        }

        // Get the first matching profile
        const profileDoc = querySnapshot.docs[0];
        const profileData = profileDoc.data();

        // Get current list_orders or initialize as empty array
        const currentOrders = profileData.list_orders || [];

        // Add the new order ID if not already present
        if (!currentOrders.includes(orderId)) {
            const updatedOrders = [...currentOrders, orderId];

            // Update the profile document
            await updateDoc(profileDoc.ref, {
                list_orders: updatedOrders
            });
        }
    } catch (error) {
        console.error('Error adding order to profile:', error);
        throw error;
    }
};

/**
 * Updates the list_orders in a Client document
 * @param {string} clientId - The client ID
 * @param {string} orderId - The order ID to add
 * @returns {Promise<void>}
 */
export const addOrderToClient = async (clientId, orderId) => {
    try {

        const clientRef = doc(db, 'Client', clientId);
        const clientSnap = await getDoc(clientRef);

        if (!clientSnap.exists()) {
            console.warn('Client not found:', clientId);
            return;
        }

        const clientData = clientSnap.data();

        // Get current list_orders or initialize as empty array
        const currentOrders = clientData.list_orders || [];

        // Add the new order ID if not already present
        if (!currentOrders.includes(orderId)) {
            const updatedOrders = [...currentOrders, orderId];

            // Update the client document
            await updateDoc(clientRef, {
                list_orders: updatedOrders
            });
        }
    } catch (error) {
        console.error('Error adding order to client:', error);
        throw error;
    }
};

/**
 * Gets all catalog orders based on user role and permissions
 * @param {string} userEmail - The user's email
 * @param {string} userRole - The user's role (Admin, Cliente, etc.)
 * @param {string} clientId - The user's client ID
 * @returns {Promise<Array>} - Array of catalog orders
 */
export const getCatalogOrdersByRole = async (userEmail, userRole, clientId) => {
    try {
        const normalizedEmail = userEmail.toLowerCase(); // Normalizza l'email

        if (userRole === 'Admin') {
            // Admin sees all orders
            const ordersQuery = query(
                collection(db, COLLECTION_NAME),
                orderBy('timestamp', 'desc')
            );
            const querySnapshot = await getDocs(ordersQuery);

            const orders = [];
            querySnapshot.forEach((doc) => {
                orders.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            return orders;
        } else if (userRole === 'Cliente') {
            // Get user's profile to check list_orders
            const profilesQuery = query(
                collection(db, 'Profiles'),
                where('_email', '==', normalizedEmail)
            );
            const profileSnapshot = await getDocs(profilesQuery);

            if (profileSnapshot.empty) {
                console.warn('Profile not found for user:', normalizedEmail);
                return [];
            }

            const profileData = profileSnapshot.docs[0].data();
            const profileOrders = profileData.list_orders || [];

            // Check if user is in mainProfileList of Client
            let clientOrders = [];
            if (clientId) {
                const clientRef = doc(db, 'Client', clientId);
                const clientSnap = await getDoc(clientRef);

                if (clientSnap.exists()) {
                    const clientData = clientSnap.data();
                    const mainProfileList = clientData.mainProfileList || [];

                    if (mainProfileList.includes(normalizedEmail)) {
                        // User is in mainProfileList, get all client orders
                        clientOrders = clientData.list_orders || [];
                    }
                }
            }

            // Combine profile orders and client orders (if user is in mainProfileList)
            const allOrderIds = [...new Set([...profileOrders, ...clientOrders])];

            if (allOrderIds.length === 0) {
                return [];
            }

            // Fetch orders by IDs
            const orders = [];
            for (const orderId of allOrderIds) {
                try {
                    const orderRef = doc(db, COLLECTION_NAME, orderId);
                    const orderSnap = await getDoc(orderRef);

                    if (orderSnap.exists()) {
                        orders.push({
                            id: orderSnap.id,
                            ...orderSnap.data()
                        });
                    }
                } catch (error) {
                    console.warn('Error fetching order:', orderId, error);
                }
            }

            // Sort by timestamp descending
            orders.sort((a, b) => {
                const timeA = a.timestamp?.seconds || 0;
                const timeB = b.timestamp?.seconds || 0;
                return timeB - timeA;
            });
            return orders;
        } else {
            // Other roles - use the existing method (profile-based)
            return await getCatalogOrdersByProfile(normalizedEmail);
        }
    } catch (error) {
        console.error('Error getting catalog orders by role:', error);
        throw error;
    }
};

/**
 * Updates the catalog order status when a quote is accepted
 * @param {string} orderId - The order document ID
 * @returns {Promise<void>}
 */
export const acceptQuote = async (orderId) => {
    try {

        const orderRef = doc(db, COLLECTION_NAME, orderId);
        const updateData = {
            status: 'quote_accepted',
            quoteResponseDate: serverTimestamp(),
            quoteResponse: 'accepted'
        };

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('Error accepting quote:', error);
        throw error;
    }
};

/**
 * Calls the POPULATE_VARIANT_FROM_SKU API in background after quote confirmation
 * @param {string} orderId - The order document ID
 * @returns {Promise<void>}
 */
export const callPopulateVariantFromSkuApi = async (orderId) => {
    const apiUrl = process.env.REACT_APP_POPULATE_VARIANT_FROM_SKU;

    try {
        console.log('Quote confirmed successfully, now calling POPULATE_VARIANT_FROM_SKU API in background...');
        console.log('CatalogOrder ID:', orderId);

        if (!apiUrl) {
            console.warn('REACT_APP_POPULATE_VARIANT_FROM_SKU is not defined in environment variables');
            return;
        }

        console.log('Making background API call to:', apiUrl);

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            mode: 'cors',
            credentials: 'omit',
            body: JSON.stringify({
                catalogOrderId: orderId
            })
        });

        console.log('Background API Response status:', response.status);

        if (response.ok) {
            const responseData = await response.json();
            console.log('Background API call successful:', responseData);
        } else {
            const errorText = await response.text();
            console.error('Background API call failed with status:', response.status, 'Error:', errorText);
        }
    } catch (apiError) {
        console.error('Error calling POPULATE_VARIANT_FROM_SKU API in background:', apiError);

        if (apiError.message === 'Failed to fetch') {
            console.error('Network error - possible CORS issue or server unavailable');
        }

        console.error('Error details:', {
            name: apiError.name,
            message: apiError.message,
            stack: apiError.stack,
            apiUrl: apiUrl,
            orderId: orderId
        });
    }
};

/**
 * Updates the catalog order status when a quote is rejected
 * @param {string} orderId - The order document ID
 * @param {string} rejectionReason - The reason for rejecting the quote
 * @returns {Promise<void>}
 */
export const rejectQuote = async (orderId, rejectionReason) => {
    try {

        const orderRef = doc(db, COLLECTION_NAME, orderId);
        const updateData = {
            status: 'quote_rejected',
            quoteResponseDate: serverTimestamp(),
            quoteResponse: 'rejected',
            rejectionReason: rejectionReason
        };

        await updateDoc(orderRef, updateData);
    } catch (error) {
        console.error('Error rejecting quote:', error);
        throw error;
    }
};

/**
 * Uploads a quote PDF file and updates the catalog order with quote URL and status
 * @param {string} orderId - The order document ID
 * @param {File} file - The PDF file to upload
 * @returns {Promise<string>} - The download URL of the uploaded file
 */
export const uploadQuoteAndUpdateOrder = async (orderId, file) => {
    try {

        // Validate file
        if (!file) {
            throw new Error('No file provided');
        }

        if (file.type !== 'application/pdf') {
            throw new Error('File must be a PDF');
        }

        // Create storage reference in /Orders/{orderId}/ folder
        const fileName = `quote_${Date.now()}.pdf`;
        const storageRef = ref(storage, `Orders/${orderId}/${fileName}`);

        // Upload file to Firebase Storage
        const uploadResult = await uploadBytes(storageRef, file);

        // Get download URL
        const downloadURL = await getDownloadURL(uploadResult.ref);

        // Update catalog order with quote URL and status
        const orderRef = doc(db, COLLECTION_NAME, orderId);
        const updateData = {
            status: 'quote_sent',
            quoteUrl: downloadURL,
            quoteUploadedAt: serverTimestamp()
        };

        await updateDoc(orderRef, updateData);

        return downloadURL;
    } catch (error) {
        console.error('❌ Error uploading quote and updating order:', error);
        throw error;
    }
}; 